import { env } from "../env";
import { getSupabase } from "../auth/supabase";

/** A refused or failed API call. `code` is the server's named error code
 * (`sole_admin`, `forbidden`, `upstream`, …) or `network` / `http_<status>`. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const FALLBACK_MESSAGE = "Something went wrong. Try again.";

/** Maps a non-2xx response body (the server's `{ code, error }` shape) to an ApiError. */
export function apiErrorFrom(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as { code?: unknown; error?: unknown };
  const code = typeof b.code === "string" ? b.code : `http_${status}`;
  const message = typeof b.error === "string" ? b.error : FALLBACK_MESSAGE;
  return new ApiError(code, message, status);
}

/** One authenticated call to the SlideBrain API; `parse` validates the success body. */
export async function apiRequest<T>(
  path: string,
  init: { method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; body?: unknown } = {},
  parse: (json: unknown) => T = (json) => json as T,
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  let res: Response;
  try {
    res = await fetchImpl(`${env.apiUrl}${path}`, {
      method: init.method ?? "GET",
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init.body !== undefined ? { "content-type": "application/json" } : {}),
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });
  } catch {
    throw new ApiError("network", "We couldn't reach SlideBrain. Check your connection and try again.", 0);
  }
  const json = await res.json().catch(() => null);
  if (!res.ok) throw apiErrorFrom(res.status, json);
  return parse(json);
}
